/**
 * @template T
 * @implements {Promise}
 */
export class Deferred {
  [Symbol.toStringTag] = "Deferred";

  /** @type {Promise<T>} */
  #promise;

  /** @type {function(T): void} */
  #resolveInternal;

  /** @type {function(?any): void} */
  #rejectInternal;

  /** @type {boolean} */
  #pendingInternal = true;

  constructor() {
    this.#promise = new Promise((resolve, reject) => {
      this.#resolveInternal = resolve;
      this.#rejectInternal = reject;
    });
  }

  /** @param {T} value */
  resolve(value) {
    if (!this.#pendingInternal) {
      return;
    }
    this.#pendingInternal = false;
    this.#resolveInternal(value);
  }

  /** @param {any} [reason] */
  reject(reason) {
    if (!this.#pendingInternal) {
      return;
    }
    this.#pendingInternal = false;
    this.#rejectInternal(reason);
  }

  /** @returns {boolean} */
  get pending() {
    return this.#pendingInternal;
  }

  /**
   * @template TResult1, TResult2
   * @param {(value: T) => TResult1 | PromiseLike<TResult1>} [onFulfilled]
   * @param {(reason: any) => TResult2 | PromiseLike<TResult2>} [onRejected]
   * @returns {Promise<TResult1 | TResult2>}
   */
  then(onFulfilled, onRejected) {
    return this.#promise.then(onFulfilled, onRejected);
  }

  /**
   * @param {(reason: any) => void} onRejected
   */
  catch(onRejected) {
    return this.#promise.catch(onRejected);
  }

  /**
   * @param {() => void} onFinally
   * @returns {Promise<T>}
   */
  finally(onFinally) {
    return this.#promise.finally(onFinally);
  }
}
